import React from "react";
import { motion } from "framer-motion";

import Stripe from "../assets/images/IntegrationTools/stripe.webp";
import Razorpay from "../assets/images/IntegrationTools/razorpay.webp";
import OpenAI from "../assets/images/IntegrationTools/openai.webp";
import Gmail from "../assets/images/IntegrationTools/gmail.webp";
import Outlook from "../assets/images/IntegrationTools/outlook.webp";
import Yahoo from "../assets/images/IntegrationTools/yahoo.webp";
import Zoho from "../assets/images/IntegrationTools/zoho.webp";


const INTEGRATIONS = [
    { name: "Stripe", src: Stripe },
    { name: "Razorpay", src: Razorpay },
    { name: "OpenAI", src: OpenAI },
    { name: "Gmail", src: Gmail },
    { name: "Outlook", src: Outlook },
    { name: "Yahoo", src: Yahoo },
    { name: "Zoho", src: Zoho },
];

function LogoItem({ name, src }) {
    return (
        <div className="flex items-center justify-center px-8 max-md:px-4">
            <img
                src={src}
                alt={name}
                className="h-16 max-md:h-14 w-auto object-contain select-none"
                loading="lazy"
                draggable={false}
            />
        </div>
    );
}

export default function IntegrationToolsWidget() {
    // Duplicate for seamless infinite scroll
    const loopList = [...INTEGRATIONS, ...INTEGRATIONS];

    return (
        <section className="w-full bg-white py-20 max-md:py-14">
            <div className="max-w-6xl mx-auto px-4 max-md:px-3">
                {/* Heading */}
                <motion.h2
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="text-[27px] md:text-5xl font-bold text-slate-900 text-center"
                >
                    Our Integration Partners
                    <br className="hidden sm:block" />
                </motion.h2>

                {/* ✅ MOBILE/TABLET: keep your grid (no animation) */}
                {/* ✅ MOBILE: slow marquee (no 3D tunnel) */}
                <div className="mt-8 md:hidden">
                    <div
                        className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white"
                        style={{
                            WebkitMaskImage:
                                "linear-gradient(90deg, transparent 0%, black 12%, black 88%, transparent 100%)",
                            maskImage:
                                "linear-gradient(90deg, transparent 0%, black 12%, black 88%, transparent 100%)",
                        }}
                    >
                        <div className="py-6">
                            <div className="itw-track itw-marquee-mobile">
                                {[...INTEGRATIONS, ...INTEGRATIONS].map((logo, idx) => (
                                    <LogoItem key={`${logo.name}-m-${idx}`} name={logo.name} src={logo.src} />
                                ))}
                            </div>
                        </div>
                    </div>

                    <style>{`
    .itw-track{
      display:flex;
      align-items:center;
      width:max-content;
      will-change:transform;
    }
    .itw-marquee-mobile{
      animation: itw-marquee-mobile 32s linear infinite;
    }
    @keyframes itw-marquee-mobile{
      0%{ transform: translateX(0); }
      100%{ transform: translateX(-50%); }
    }
  `}</style>
                </div>


                {/* ✅ DESKTOP: infinite right-to-left tunnel marquee */}
                <div className="hidden md:block mt-10">
                    <div
                        className="relative overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_20px_60px_rgba(15,23,42,0.06)]"
                        style={{
                            // fade edges (so it feels like “tunnel”)
                            WebkitMaskImage:
                                "linear-gradient(90deg, transparent 0%, black 12%, black 88%, transparent 100%)",
                            maskImage:
                                "linear-gradient(90deg, transparent 0%, black 12%, black 88%, transparent 100%)",
                        }}
                    >
                        {/* Tunnel “portals” (left + right) */}
                        <div className="pointer-events-none absolute inset-y-0 left-0 w-40">
                            <div className="absolute inset-0 bg-gradient-to-r from-white via-white/70 to-transparent" />
                            <div className="absolute left-6 top-1/2 -translate-y-1/2 w-24 h-24 rounded-full bg-indigo-200/40 blur-2xl" />
                            <div className="absolute left-10 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-purple-200/50 blur-xl" />
                        </div>
                        <div className="pointer-events-none absolute inset-y-0 right-0 w-40">
                            <div className="absolute inset-0 bg-gradient-to-l from-white via-white/70 to-transparent" />
                            <div className="absolute right-6 top-1/2 -translate-y-1/2 w-24 h-24 rounded-full bg-purple-200/40 blur-2xl" />
                            <div className="absolute right-10 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-indigo-200/50 blur-xl" />
                        </div>

                        {/* Subtle blueprint grid behind (optional, classic) */}
                        <svg
                            className="pointer-events-none absolute inset-0 w-full h-full opacity-[0.06]"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <defs>
                                <pattern
                                    id="itw-grid"
                                    width="48"
                                    height="48"
                                    patternUnits="userSpaceOnUse"
                                >
                                    <path
                                        d="M 48 0 L 0 0 0 48"
                                        fill="none"
                                        stroke="rgb(99 102 241)"
                                        strokeWidth="1"
                                    />
                                </pattern>
                            </defs>
                            <rect width="100%" height="100%" fill="url(#itw-grid)" />
                        </svg>

                        {/* Track */}
                        <div className="relative py-10">
                            <div className="itw-perspective">
                                <div className="itw-track itw-marquee">
                                    {loopList.map((logo, idx) => (
                                        <LogoItem
                                            key={`${logo.name}-${idx}`}
                                            name={logo.name}
                                            src={logo.src}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Styles for smooth continuous loop + tunnel vibe */}
                    <style>{`
            /* Slight 3D depth for “tunnel” illusion */
            .itw-perspective{
              perspective: 900px;
              transform-style: preserve-3d;
            }

            /* The moving row (flex) */
            .itw-track{
              display: flex;
              align-items: center;
              width: max-content;
              will-change: transform;
              transform: translateZ(0) rotateY(-6deg);
              filter: saturate(1.02);
            }

            /* Continuous loop:
               We duplicated the list, so moving by 50% of its total length loops perfectly. */
            .itw-marquee{
              animation: itw-marquee 22s linear infinite;
            }

            @keyframes itw-marquee{
              0%   { transform: translateX(0) translateZ(0) rotateY(-6deg); }
              100% { transform: translateX(-50%) translateZ(0) rotateY(-6deg); }
            }

            /* Optional: pause on hover (feels premium) */
            .itw-marquee:hover{
              animation-play-state: paused;
            }
          `}</style>
                </div>
            </div>
        </section>
    );
}
